import { useEffect, useMemo, useRef, useState } from 'react'
import { useNow, useWorkspace } from '../store/AppContext'
import { ClientPicker } from '../components/ClientPicker'
import { ProjectPicker } from '../components/ProjectPicker'
import { EmptyState } from '../components/ui'
import { ProspectAdmin } from './ProspectAdmin'
import {
  clock,
  DAY_SEC,
  dayKey,
  dayLabel,
  fitDuration,
  formatDur,
  hms,
  hmsExact,
  MAX_SESSION_SEC,
  nowRoundedToMinute,
  parseClock,
  keysBetween,
  parseDurationInput,
  secOfDay,
  spellDuration,
  shortDate,
  weekKeys,
  todayKey,
} from '../lib/time'
import {
  chargesForTime,
  formatMoney,
  overlappingIds,
  parseMoneyInput,
  rateOnDate,
  workspaceCurrency,
} from '../lib/billing'
import { useRangeBilling } from '../lib/team-data'
import * as db from '../lib/db'
import { blockedByRules, entryMatches, idsOfKind, SWATCHES, type ClientKind } from '../lib/types'
import type { BillingRow } from '../lib/db'
import type { DayKey, Entry } from '../lib/types'

type Filter = 'all' | 'billable' | 'nonbillable'

/** Today first: on this screen the usual question is "what have I done today". */
const TIMER_RANGES = ['All', 'Today', 'This week', 'This month', 'Custom'] as const
type TimerRange = (typeof TIMER_RANGES)[number]

/** The longest custom range, so a mistyped year cannot ask for a decade. */
const MAX_RANGE_DAYS = 366

/**
 * The length a fixed-fee entry starts at, when it has none.
 *
 * A flat fee does not depend on the duration -- that is the whole point of it
 * -- but an entry still needs one, and the manual bar opens with start and end
 * both set to now. So switching the fee on was followed by "Start and end are
 * the same", which is a true thing to say at a useless moment. Half an hour is
 * a plausible default to correct rather than a zero to fix.
 */
const FEE_DEFAULT_SEC = 30 * 60

/**
 * The time log, for one population or the other.
 *
 * Timer and Prospects are the same screen with `kind` set differently, and
 * deliberately so: logging an hour against a prospect is the same act as
 * logging one against a client, and two copies of a fourteen-hundred-line
 * screen would have drifted apart within a week.
 *
 * What `kind` changes is the population, end to end -- which entries the log
 * shows, which rows the two pickers offer, what a new client created from
 * here becomes, what the words say, and which rows the export covers. The two
 * never appear together on either side.
 */
export function TimerScreen({ kind = 'client' }: { kind?: ClientKind } = {}) {
  const app = useWorkspace()
  const now = useNow()
  const {
    ws,
    me,
    draft,
    setDraft,
    manualMode,
    setManualMode,
    running,
    flash,
    setModal,
    act,
    confirm,
    canSeeAll,
    canSetRates,
    setScreen,
  } = app
  const S = ws.settings
  const card = ws.appearance.timerStyle === 'card'

  const prospecting = kind === 'prospect'
  const noun = prospecting ? 'prospect' : 'client'

  /**
   * Does this client id belong on this screen?
   *
   * Entries with no client at all have no kind, and they stay on the client
   * side -- that is where unassigned time has always been listed, and moving
   * it to neither screen would be losing it rather than separating it.
   */
  const inScope = useMemo(() => {
    const ids = idsOfKind(ws.clients, kind)
    return (clientId: string | null | undefined) =>
      clientId === null || clientId === undefined ? kind === 'client' : ids.has(clientId)
  }, [ws.clients, kind])

  /*
   * A fresh manual entry ends NOW and is zero long, so the only thing left to
   * say is how long it was. The old 9:00-10:30 default was a guess that had to
   * be cleared before it could be replaced.
   */
  const freshManualTime = () => clock(nowRoundedToMinute(), S.timeFormat)
  const [manualStart, setManualStart] = useState(freshManualTime)
  const [manualEnd, setManualEnd] = useState(freshManualTime)
  const [manualDay, setManualDay] = useState(todayKey())
  /*
   * A flat fee, set while the entry is being written.
   *
   * It was only reachable by adding the entry first and then opening it to
   * edit -- which meant logging an hour at the hourly rate and correcting it a
   * moment later, with a wrong figure in the reports in between.
   */
  const [manualFeeOn, setManualFeeOn] = useState(false)
  const [manualFee, setManualFee] = useState('')

  /** The fee already on the running entry, if there is one running. */
  const runningFee = running?.fixedAmount ?? null

  /*
   * Seed the box from whatever the bar is pointed at, once per timer.
   *
   * A fee set before Start is written to the entry, so once the timer begins
   * the truth lives on the row rather than in this box -- and after a reload
   * the box is all the screen has. Keyed to the timer so it syncs when one
   * starts or stops, and then leaves the typing alone.
   */
  const feeSyncedFor = useRef('')

  useEffect(() => {
    const key = running ? 'run:' + running.at : 'idle'
    if (feeSyncedFor.current === key) return
    feeSyncedFor.current = key
    setManualFeeOn(runningFee !== null)
    setManualFee(runningFee !== null ? String(runningFee) : '')
  }, [running, runningFee])

  /*
   * While a timer runs the entry already exists, so a change to the fee is
   * written at once rather than held until Stop. An amount that is not yet a
   * number is not sent -- somebody halfway through typing "25" has not asked
   * for anything yet.
   */
  const pushFee = (on: boolean, text: string) => {
    if (!running) return
    const amount = on ? parseMoneyInput(text) : null
    if (on && amount === null) return
    void act.updateTimer({ fixedAmount: amount })
  }
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [visibleDays, setVisibleDays] = useState(7)
  /*
   * Which days to show. "All" is the default and is not in the list the
   * Reports screen offers -- this screen showed everything before, and
   * silently hiding older entries behind a default range would be a
   * regression dressed up as a feature.
   */
  const [range, setRange] = useState<TimerRange>('All')
  const [rangeFrom, setRangeFrom] = useState(todayKey)
  const [rangeTo, setRangeTo] = useState(todayKey)

  /**
   * Narrows the project picker beside it. Not part of the entry: you track
   * against a project, and the project already knows its client.
   */
  const [filterClient, setFilterClient] = useState<string | null>(null)

  /**
   * Folded-away client groups.
   *
   * Keyed by day and client together: the same client turns up on most days,
   * and folding Tuesday's away should not fold Wednesday's with it.
   */
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())

  const toggleGroup = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  /* The description of a running timer lives on the server, so edits are
     debounced rather than sent on every keystroke. */
  const [liveDesc, setLiveDesc] = useState<string | null>(null)
  const pending = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const dirty = useRef<string | null>(null)

  useEffect(() => {
    if (!running) {
      setLiveDesc(null)
      dirty.current = null
      clearTimeout(pending.current)
    }
  }, [running])
  useEffect(() => () => clearTimeout(pending.current), [])

  const flushDesc = async () => {
    if (dirty.current === null) return
    const value = dirty.current
    dirty.current = null
    clearTimeout(pending.current)
    await act.updateTimer({ desc: value })
  }

  /**
   * A person has ONE running timer, and both screens render it.
   *
   * That is correct as data -- start_timer closes whatever was running
   * before inserting, so two at once cannot exist -- and wrong as a display.
   * A client timer appearing on the Prospects screen put a client's name in
   * the prospect picker and its project beside it, which is the crossover
   * this whole feature exists to prevent, and it read as two timers counting
   * the same minutes.
   *
   * So each screen shows the timer only if it belongs to that screen's
   * population. When it does not, the bar is replaced by a notice saying
   * where it is running, because hiding it entirely would be worse: pressing
   * Start here would then stop a timer the screen never mentioned.
   */
  const runningClientId = running?.projectId
    ? (ws.projects.find((p) => p.id === running.projectId)?.clientId ?? null)
    : null
  const elsewhere = Boolean(running) && !inScope(runningClientId)

  const manualAllowed = S.allowManual
  const inManual = manualMode && manualAllowed && !running

  const elapsed = running ? Math.max(0, Math.floor((now - running.at) / 1000)) : 0
  const ma = parseClock(manualStart)
  const mb = parseClock(manualEnd)
  const manualSec = ma !== null && mb !== null ? (mb - ma + DAY_SEC) % DAY_SEC : null

  const descValue = running ? (liveDesc ?? running.desc) : draft.desc
  const setDesc = (v: string) => {
    if (!running) {
      setDraft({ desc: v })
      return
    }
    setLiveDesc(v)
    dirty.current = v
    clearTimeout(pending.current)
    pending.current = setTimeout(() => void flushDesc(), 600)
  }

  const pickProject = (projectId: string | null, task: string) => {
    if (running) {
      void act.updateTimer({ projectId, task })
      return
    }
    const p = ws.projects.find((x) => x.id === projectId)
    setDraft({ projectId, task, billable: p ? p.billable : draft.billable })
  }

  /**
   * Whatever project the bar is pointed at, running or not.
   *
   * A timer running on the other side is not what this bar is pointed at.
   * Without that exclusion the effect below would sync the picker to its
   * client -- putting a client's name in the prospect picker, which is the
   * crossover the screen is built to prevent, and leaving it there after
   * the timer stopped.
   */
  const activeProjectId = running && !elsewhere ? running.projectId : draft.projectId

  /*
   * Show the client that the active project belongs to.
   *
   * A project belongs to exactly one client, so once a project is set the
   * client is not a separate choice -- it is a fact about that project. But
   * the client picker is this screen's own state and nothing updated it when
   * a project arrived from anywhere else: continuing an entry set the project
   * and left the picker reading "Client", which looks like no client was
   * recorded when one plainly was. Same for duplicating, and for a timer
   * already running when the screen loads.
   *
   * An effect rather than a value worked out while rendering, because this
   * has to stay overridable. Derived, the picker would snap back the instant
   * somebody chose a different client, and their click would look broken.
   * Keyed to the project, it syncs when the project moves and then leaves a
   * deliberate choice alone.
   *
   * The ref is what keeps it to once per project. ws.projects gets a new
   * identity every time the workspace reloads, and without it each reload
   * would re-assert the client over somebody who had just widened it.
   *
   * It follows the project all the way, including to NO client when the
   * project has none -- otherwise continuing an entry on an unassigned
   * project would leave the last client sitting there, which is the same
   * false reading in the other direction. Because this runs only when the
   * project moves, it still leaves alone somebody who widened the picker to
   * "All clients" by hand.
   */
  const clientSyncedFor = useRef<string | null>(null)

  useEffect(() => {
    if (!activeProjectId) {
      clientSyncedFor.current = null
      return
    }
    if (clientSyncedFor.current === activeProjectId) return
    clientSyncedFor.current = activeProjectId

    const clientId = ws.projects.find((p) => p.id === activeProjectId)?.clientId
    setFilterClient(clientId ?? null)
  }, [activeProjectId, ws.projects])

  // A person who does not charge cannot make an entry billable, so the
  // control says so instead of offering a choice with no effect.
  /**
   * The currency this person is billed in, for the fee box.
   *
   * Their own effective rate, not whichever rate happened to be first in the
   * workspace. Falls back to the workspace's when they have no rate yet.
   */
  /** This person's rate history, in the shape rateOnDate reads. */
  const myRates = useMemo(
    () =>
      ws.rates
        .filter((r) => r.userId === me.id)
        .map((r) => ({
          hourly_rate: r.hourlyRate,
          currency: r.currency,
          effective_from: r.effectiveFrom,
        })),
    [ws.rates, me.id],
  )

  const manualCurrency = useMemo(
    () => rateOnDate(myRates, manualDay)?.currency ?? workspaceCurrency(ws.rates),
    [myRates, manualDay, ws.rates],
  )

  /**
   * Would this entry be charged at nothing?
   *
   * Refusing to track time because somebody has no rate is only right when
   * the time would actually have earned something. So this asks the real
   * question -- is there any price for this entry at all -- rather than the
   * easy one.
   *
   * The pricing chain is: entry fee, entry override, task fixed, project
   * fixed, user hourly, project hourly. Four of those are checkable here; a
   * TASK or PROJECT fixed price is not, because the snapshot does not carry
   * the fixed-price tables. Those cases fall through to blocked, which is
   * the direction that can be argued with rather than the one that silently
   * logs an hour worth nothing.
   */
  const pricedAtNothing = (projectId: string | null, fee: number | null, day: DayKey): boolean => {
    // Not being charged for is not the same as being charged nothing.
    if (!billValue) return false
    if (fee !== null) return false
    if (rateOnDate(myRates, day)) return false
    const p = projectId ? ws.projects.find((x) => x.id === projectId) : null
    return !(p && p.rate > 0)
  }

  /*
   * Said to the person in front of it, who in most cases cannot fix it.
   *
   * Setting a rate is owner, admin and manager only -- canSetRates -- so
   * telling a member to go and set theirs would be sending them somewhere
   * they are not allowed to go. It names who can, and the way out that does
   * not need anybody: mark the time as not charged.
   */
  const noRateMessage = canSetRates
    ? 'You have no charge-out rate yet, so this time would be billed at nothing. Set your rate on the Team screen, or mark this entry as not charged.'
    : 'You have no charge-out rate yet, so this time would be billed at nothing. Ask an owner or manager to set your rate, or mark this entry as not charged.'

  /*
   * Prospect time starts non-billable, client time starts billable.
   *
   * Keyed on the side rather than set once, so arriving here points the bar
   * at the right default -- and the store's own reset after each entry
   * matches it, so the second entry starts where the first did.
   *
   * It is a default, not a rule: the control is still there and still works,
   * because a prospect occasionally does agree to pay for something before
   * the rest of it is won.
   */
  useEffect(() => {
    if (running) return
    setDraft({ billable: kind === 'client' })
    // setDraft is stable and draft.billable is deliberately not a dependency:
    // this sets a default when the side changes, and must not undo a choice
    // made afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, running])

  const canCharge = me.billable
  const billValue = canCharge && (running ? running.billable : draft.billable)
  /*
   * Turning charging OFF asks first; turning it on does not.
   *
   * The two mistakes are not the same size. Billing for something by accident
   * appears on the invoice, where somebody reads it and it gets corrected.
   * NOT billing is silent -- the hours still sit there looking logged, and the
   * money is simply never asked for. A mis-click on a 24px target should not
   * be able to do that quietly.
   */
  const toggleBill = () => {
    if (!canCharge) return

    const applyTo = (billable: boolean) => {
      if (running) void act.updateTimer({ billable })
      else setDraft({ billable })
    }

    if (!(running ? running.billable : draft.billable)) {
      applyTo(true)
      return
    }

    confirm({
      title: 'Stop charging for this time?',
      body:
        'The hours are still recorded and still counted, but they will add ' +
        'nothing to the invoice. You can switch it back at any time.',
      cta: 'Make it non-billable',
      onConfirm: () => applyTo(false),
    })
  }

  /**
   * What the workspace rules say about creating time right now, or null.
   *
   * One function for every path that makes an entry: the timer, the manual
   * range, and typing a length into the clock. requireProject used to be
   * checked on the manual path alone, so "Timers can't start without one
   * selected" was simply not true of the timer -- the setting said one thing
   * and the Start button did another.
   *
   * A client is a property of the PROJECT, never of the entry, so requiring a
   * client is a requirement on the project chosen.
   */
  const ruleBlock = (): string | null =>
    blockedByRules(S, draft.projectId ? ws.projects.find((p) => p.id === draft.projectId) : null)

  /*
   * Re-anchor on entering manual mode. The initial state is set once at mount,
   * so a screen left open all morning would otherwise offer a "now" from
   * whenever the tab was opened.
   */
  useEffect(() => {
    if (!inManual) return
    const t = clock(nowRoundedToMinute(), S.timeFormat)
    setManualStart(t)
    setManualEnd(t)
    setManualDay(todayKey())
  }, [inManual, S.timeFormat])

  /**
   * The project a prospect's time hangs on, made the first time it is needed.
   *
   * An entry reaches its client only through a project -- time_entries has a
   * project_id and nothing else -- so with the picker gone from this screen
   * there has to be one anyway, or prospect time would attach to nothing and
   * disappear from the very screen that logged it.
   *
   * One per prospect, named after it, created on the first entry and reused
   * forever after. Nobody is asked about it: on this side the prospect IS
   * the thing being worked on, and a second name for it would be a question
   * with one possible answer.
   */
  const prospectProject = async (): Promise<string | null> => {
    if (!filterClient) {
      flash('Choose a prospect first.')
      return null
    }

    const existing = ws.projects.find((p) => p.clientId === filterClient && p.status === 'active')
    if (existing) return existing.id

    const name = ws.clients.find((c) => c.id === filterClient)?.name ?? 'Prospect'
    try {
      const id = await db.createProject(ws.id, { name, clientId: filterClient, color: SWATCHES[0] })
      // Pull it into the snapshot, so the entry about to be written resolves
      // its name and the next click finds it rather than making a second.
      await act.updateWorkspace({})
      return id
    } catch (err) {
      flash(err instanceof Error ? err.message : 'Could not set up this prospect.')
      return null
    }
  }

  const primary = async () => {
    // Stopping is never blocked: a rule added while a timer runs must not
    // trap the time already on the clock.
    if (running) {
      await flushDesc()
      const stopped = await act.stopTimer()
      // The project and task are cleared in the store; the client filter is
      // local to this screen, so it is reset here or it would keep narrowing
      // the project picker for the next entry.
      if (stopped) setFilterClient(null)
      return
    }

    /*
     * Resolved before the rules are checked, because on this side the
     * project is not a choice the person makes -- it follows from the
     * prospect, and a "pick a project" refusal would name a control that is
     * not on the screen.
     */
    let projectId = draft.projectId
    if (prospecting) {
      const resolved = await prospectProject()
      if (!resolved) return
      projectId = resolved
    }

    const blocked = prospecting
      ? blockedByRules(S, { name: '', clientId: filterClient })
      : ruleBlock()
    if (blocked) {
      flash(blocked)
      return
    }

    if (inManual) {
      if (ma === null || mb === null) {
        flash('Use times like 9:00, 9:30 am or 17:45.')
        return
      }
      if (!manualSec) {
        flash('Start and end are the same — that would log zero time.')
        return
      }
      // Same rule the entry form uses: a fee switched on has to have an
      // amount, or the entry silently charges nothing.
      const fee = !prospecting && manualFeeOn && billValue ? parseMoneyInput(manualFee) : null
      if (!prospecting && manualFeeOn && billValue && fee === null) {
        flash('Enter the fixed fee, or switch it off to charge by the hour.')
        return
      }

      if (pricedAtNothing(projectId, fee, manualDay)) {
        flash(noRateMessage)
        return
      }

      const added = await act.addManual({
        start: ma,
        end: mb,
        day: manualDay,
        fixedAmount: fee,
        projectId,
      })
      if (added) {
        // Back to a fresh zero-length entry ending now, ready for the next one.
        const t = freshManualTime()
        setManualStart(t)
        setManualEnd(t)
        setManualDay(todayKey())
      }
      // The store clears the project and task; the client filter is this
      // screen's state, so it is reset alongside -- otherwise it keeps
      // narrowing the project picker for the next entry.
      if (added) {
        setFilterClient(null)
        setManualFeeOn(false)
        setManualFee('')
      }
      return
    }
    // Same rule as the manual bar: a fee switched on has to have an amount.
    const startFee = !prospecting && manualFeeOn && billValue ? parseMoneyInput(manualFee) : null
    if (!prospecting && manualFeeOn && billValue && startFee === null) {
      flash('Enter the fixed fee, or switch it off to charge by the hour.')
      return
    }

    /*
     * Checked against TODAY, which is when a timer starts. A rate that
     * begins tomorrow does not price an hour worked this afternoon, and
     * rateOnDate already knows that.
     */
    if (pricedAtNothing(projectId, startFee, todayKey())) {
      flash(noRateMessage)
      return
    }

    await act.startTimer(startFee, projectId)
  }

  const primaryLabel = inManual ? 'Add' : running ? 'Stop' : 'Start'

  /**
   * Setting the big clock by typing a length into it.
   *
   * Running: the start moves, so it has been running for exactly that long.
   * Idle: that much time is logged, ending now -- which is the point of the
   * field, since otherwise adding forty minutes means working out two clock
   * times and typing both.
   *
   * The entry is anchored on the day it STARTS, so a length reaching back past
   * midnight lands on the right day and keeps its exact duration: addManual
   * takes the difference modulo a day, which recovers anything under 24 hours
   * intact, and the limit is 10.
   */
  /**
   * Typing a length into the manual range.
   *
   * The start is kept and the END moves, so the two clock fields still say
   * what they always said -- the range is the record, the length is just a
   * faster way to express it. Wrapped modulo a day because a manual range may
   * legitimately cross midnight, which is how manualSec already reads it.
   */
  const setManualLength = (seconds: number) => {
    const to = parseClock(manualEnd)
    if (to === null) {
      flash('Set an end time first, then the length.')
      return
    }
    /*
     * The START moves, not the end. The end defaults to now, so growing
     * forward would put the entry in the future -- which validate_time_entry
     * refuses outright. Working backwards from when you finished is also how
     * people describe it: "that took me an hour and a half".
     */
    setManualStart(clock((to - seconds + DAY_SEC) % DAY_SEC, S.timeFormat))
  }

  const setElapsedTo = (seconds: number) => {
    if (running) {
      void act.updateTimer({ at: Date.now() - seconds * 1000 })
      return
    }

    // This writes an entry, so the same rules apply as to Start and Add.
    const blocked = ruleBlock()
    if (blocked) {
      flash(blocked)
      return
    }

    const endMs = Date.now()
    const startMs = endMs - seconds * 1000
    void act
      .addManual({
        day: dayKey(new Date(startMs)),
        start: secOfDay(startMs),
        end: secOfDay(endMs),
      })
      .then((added) => {
        if (added) setFilterClient(null)
      })
  }

  /**
   * The days the range admits, or null for all of them.
   *
   * A Set rather than a list: this is asked once per entry, and a month is
   * thirty-one scans otherwise.
   */
  const rangeDays = useMemo<Set<DayKey> | null>(() => {
    if (range === 'All') return null
    if (range === 'Today') return new Set([todayKey()])
    if (range === 'This week') return new Set(weekKeys(0, S.weekStart))
    if (range === 'Custom') return new Set(keysBetween(rangeFrom, rangeTo, MAX_RANGE_DAYS))

    const t = new Date()
    const days = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate()
    return new Set(
      Array.from({ length: days }, (_, i) =>
        dayKey(new Date(t.getFullYear(), t.getMonth(), i + 1)),
      ),
    )
  }, [range, rangeFrom, rangeTo, S.weekStart])

  /* ---- entries that share clock time with another of yours ---- */
  // Overlapping is allowed on purpose, but the hours are summed twice, so the
  // rows that cause it are marked. Computed once over the whole list because
  // an overlap is a property of a PAIR -- a row cannot see it alone.
  const overlaps = useMemo(
    () => overlappingIds(ws.entries.filter((e) => e.userId === me.id)),
    [ws.entries, me.id],
  )

  /* ---- my entries, grouped by day ---- */
  const groups = useMemo(() => {
    // Resolved once rather than per entry: both filters below reach for the
    // project, and the client is reached through it.
    const byProject = new Map(ws.projects.map((p) => [p.id, p]))
    const byClient = new Map(ws.clients.map((c) => [c.id, c]))

    const mine = ws.entries
      .filter((e) => e.userId === me.id)
      /*
       * This side of the line, before anything else.
       *
       * Not a filter the user can clear: there is no view of this screen
       * that shows the other population, so it is applied first and the
       * chips below only ever narrow what is left.
       */
      .filter((e) => inScope(e.projectId ? byProject.get(e.projectId)?.clientId : null))
      .filter((e) => !rangeDays || rangeDays.has(e.day))
      .filter((e) => {
        if (filter === 'all') return true
        const pb = e.projectId ? byProject.get(e.projectId)?.billable : undefined
        const charges = chargesForTime(e.billable, me.billable, pb)
        return filter === 'billable' ? charges : !charges
      })
      .filter((e) => {
        const p = e.projectId ? byProject.get(e.projectId) : undefined
        const c = p?.clientId ? byClient.get(p.clientId) : undefined
        return entryMatches(search, {
          desc: e.desc,
          tags: e.tags,
          projectName: p?.name,
          clientName: c?.name,
        })
      })
      .sort((a, b) => (a.day === b.day ? b.start - a.start : a.day < b.day ? 1 : -1))

    /**
     * Day, then client, then entries.
     *
     * The day stays the outer grouping: this is a log of when work happened,
     * and burying that under client would make "what did I do on Tuesday"
     * unanswerable. Within a day, entries gather under the client they were
     * billed to, matching the Projects screen, the picker and the timesheet.
     *
     * Order inside a client stays newest-first, so the chronology survives
     * where it still means something.
     */
    const byDay: {
      key: string
      sec: number
      clients: { id: string; name: string; entries: Entry[]; sec: number }[]
    }[] = []

    for (const e of mine) {
      let day = byDay.find((x) => x.key === e.day)
      if (!day) {
        day = { key: e.day, sec: 0, clients: [] }
        byDay.push(day)
      }
      day.sec += e.dur

      const project = ws.projects.find((x) => x.id === e.projectId)
      const c = project?.clientId ? ws.clients.find((x) => x.id === project.clientId) : null
      const id = c?.id ?? '~none'

      let group = day.clients.find((x) => x.id === id)
      if (!group) {
        group = { id, name: c?.name ?? `No ${noun}`, entries: [], sec: 0 }
        day.clients.push(group)
      }
      group.entries.push(e)
      group.sec += e.dur
    }

    // Alphabetical within the day, with unassigned work last rather than
    // sorted into the Ns.
    for (const day of byDay) {
      day.clients.sort((a, b) => {
        if (a.id === '~none') return 1
        if (b.id === '~none') return -1
        return a.name.localeCompare(b.name)
      })
    }

    return { byDay, count: mine.length }
  }, [ws.entries, ws.projects, ws.clients, me.id, me.billable, filter, search, rangeDays, inScope, noun])

  const shown = groups.byDay.slice(0, visibleDays)

  /**
   * What the rows on screen charged, keyed by entry id.
   *
   * Scoped to the days actually RENDERED rather than the whole filter range,
   * so "All" over a year of history does not fetch a year of prices to label
   * the fortnight that is visible. "Show earlier days" widens it.
   *
   * The signature is what forces a re-price after an edit on this very screen.
   */
  const billRevision = useMemo(
    () =>
      shown
        .flatMap((d) => d.clients.flatMap((c) => c.entries))
        .map((en) =>
          [
            en.id,
            en.dur,
            en.billable ? 1 : 0,
            en.projectId ?? '',
            en.task,
            en.rateOverride ?? '',
            en.fixedAmount ?? '',
          ].join(':'),
        )
        .join('|'),
    [shown],
  )

  /**
   * Download what this screen is showing.
   *
   * The dates come from the log rather than from the range chips, because
   * "All" has no endpoints to send -- the widest day present IS the range in
   * that case, and for every other chip the two agree.
   *
   * The server is told which population to cover, and it is the same server
   * and the same permission check as the Reports export: one place decides
   * what a CSV may contain. Prospect time never reaches the client file, and
   * this file holds nothing else.
   */
  const exportLog = () => {
    const days = groups.byDay.map((d) => d.key)
    if (!days.length) {
      flash(`Nothing to export${prospecting ? ' for any prospect' : ''} in this range`)
      return
    }

    // byDay is newest-first; the export wants from, then to.
    const from = days[days.length - 1]
    const to = days[0]

    act.exportCsv(from, to, 'mine', {
      kind,
      clientId: filterClient,
      billable: filter === 'all' ? null : filter === 'billable',
    })
    flash(`Exporting ${groups.count} ${groups.count === 1 ? 'entry' : 'entries'}`)
  }

  const { billing } = useRangeBilling(
    ws.id,
    shown.length ? { from: shown[shown.length - 1].key, to: shown[0].key } : null,
    true,
    billRevision,
  )

  /**
   * Whatever the database priced in, so the label matches the figure. Rates
   * carry their own currency and nothing forces them to agree, so reading one
   * off ws.rates could label a figure in a currency it was never computed in.
   */
  const currency = useMemo(() => {
    for (const r of billing.values()) if (r.currency) return r.currency
    return workspaceCurrency(ws.rates)
  }, [billing, ws.rates])

  return (
    <section className="screen">
      <div className={running && !elsewhere ? 'tracker tracker--live' : 'tracker'}>
        {elsewhere ? (
          <div
            className="row"
            style={{ gap: 12, padding: '18px 22px', flexWrap: 'wrap', alignItems: 'center' }}
          >
            <span className="live-dot" aria-hidden />
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {prospecting ? 'A client timer is running' : 'A prospect timer is running'}
              </div>
              <div className="trunc" style={{ fontSize: 12.5, color: 'var(--mut)' }}>
                {ws.projects.find((p) => p.id === running?.projectId)?.name ?? 'No project'}
                {running ? ` · since ${clock(secOfDay(running.at), S.timeFormat)}` : ''}
                {' · '}
                {hms(elapsed)}
              </div>
            </div>
            {/* One timer at a time is the rule, so this screen offers the two
                things that are actually possible: go and look at it, or end
                it and start fresh here. Starting here without saying so would
                stop it silently. */}
            <button
              type="button"
              className="btn"
              onClick={() => setScreen(prospecting ? 'timer' : 'prospects')}
            >
              Go to {prospecting ? 'Timer' : 'Prospects'}
            </button>
            <button type="button" className="btn btn--primary" onClick={() => void act.stopTimer()}>
              Stop it
            </button>
          </div>
        ) : card ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 26, padding: '26px 26px 22px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
              <div className="hero-digits">
                {inManual ? (
                  manualSec === null ? (
                    '—'
                  ) : (
                    hms(manualSec)
                  )
                ) : (
                  <DurationEdit
                    sec={running ? elapsed : 0}
                    label={running ? hmsExact(elapsed) : hms(0)}
                    seed={running ? hmsExact(elapsed) : undefined}
                    inputClassName="hero-input"
                    title={
                      running
                        ? 'Click to correct how long this has been running'
                        : 'Click to log a length ending now — type 90 for 90 minutes'
                    }
                    onCommit={setElapsedTo}
                  />
                )}
              </div>
              <div className={running ? 'hero-status hero-status--live' : 'hero-status'}>
                {running ? 'Running' : inManual ? 'Manual entry' : 'Ready'}
              </div>
              {/* Wall clock under the elapsed time: the elapsed figure answers
                  "how long", this answers "when", and people reading a
                  timesheet back want both. Ticks from the shared `now`. */}
              <div style={{ marginTop: 2 }}>
                <div
                  className="mono"
                  style={{ fontSize: 17, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                >
                  {new Date(now).toLocaleTimeString(undefined, {
                    hour: S.timeFormat === '24h' ? '2-digit' : 'numeric',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: S.timeFormat !== '24h',
                  })}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--mut)' }}>
                  {new Date(now).toLocaleDateString(undefined, {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </div>
              </div>
            </div>
            <div style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                className="input"
                style={{ background: 'var(--canvas)', fontSize: 15, padding: '11px 13px', borderRadius: 9 }}
                value={descValue}
                onChange={(e) => setDesc(e.target.value)}
                onBlur={() => void flushDesc()}
                onKeyDown={(e) => e.key === 'Enter' && void primary()}
                placeholder="What are you working on?"
                aria-label="What are you working on?"
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <ClientPicker
                  kind={kind}
                  clientId={filterClient}
                  onPick={setFilterClient}
                  align="left"
                />
                {!prospecting && (
                  <ProjectPicker
                    kind={kind}
                    projectId={running ? running.projectId : draft.projectId}
                    task={running ? running.task : draft.task}
                    onPick={pickProject}
                    align="left"
                    clientId={filterClient}
                  />
                )}
                <BillButton on={billValue} onClick={toggleBill} />
                {manualAllowed && (
                  <div className="seg" style={{ marginLeft: 'auto' }}>
                    <button type="button" aria-pressed={!manualMode} onClick={() => setManualMode(false)}>
                      ▶ Timer
                    </button>
                    <button
                      type="button"
                      aria-pressed={manualMode}
                      onClick={() => setManualMode(true)}
                      disabled={!!running}
                    >
                      ☰ Manual
                    </button>
                  </div>
                )}
              </div>
              {inManual && (
                <ManualFields
                  start={manualStart}
                  end={manualEnd}
                  day={manualDay}
                  setStart={setManualStart}
                  setEnd={setManualEnd}
                  setDay={setManualDay}
                  sec={manualSec}
                  onDuration={setManualLength}
                  large
                />
              )}
              <button
                type="button"
                className={running ? 'btn btn--ink btn--block btn--lg' : 'btn btn--primary btn--block btn--lg'}
                onClick={() => void primary()}
              >
                {primaryLabel}
              </button>
            </div>
          </div>
        ) : (
          <div className="tracker-bar">
            <input
              className="desc-input"
              value={descValue}
              onChange={(e) => setDesc(e.target.value)}
              onBlur={() => void flushDesc()}
              onKeyDown={(e) => e.key === 'Enter' && void primary()}
              placeholder="What are you working on?"
              aria-label="What are you working on?"
            />
            <ClientPicker kind={kind} clientId={filterClient} onPick={setFilterClient} />
            {!prospecting && (
              <ProjectPicker
                kind={kind}
                projectId={running ? running.projectId : draft.projectId}
                task={running ? running.task : draft.task}
                onPick={pickProject}
                clientId={filterClient}
              />
            )}
            <BillButton on={billValue} onClick={toggleBill} />

            {/* Next to the $ because it is the same decision: whether this
                time is charged, and then how.

                In both modes. It was manual-only on the reasoning that a
                running timer has no length for a fee to replace -- which is
                exactly backwards: a flat fee does not depend on the length,
                so there is nothing to wait for. Deciding it up front is the
                normal case. */}
            {/* No fixed fee on the prospect side. A flat fee is a price
                agreed with somebody who has agreed to pay, which is the one
                thing a prospect has not done. */}
            {!prospecting && (
              <div className="row" style={{ gap: 6, flexShrink: 0 }}>
                <button
                  type="button"
                  className="chip"
                  aria-pressed={manualFeeOn}
                  disabled={!billValue}
                  title={
                    billValue
                      ? 'Charge one amount for this entry, however long it took'
                      : 'This entry is not charged, so a fixed fee would do nothing'
                  }
                  onClick={() => {
                    const next = !manualFeeOn
                    setManualFeeOn(next)

                    /*
                     * Give a MANUAL entry a length if it has none. Only on the
                     * way ON, and only from zero: a range already typed is the
                     * person's answer and is not overwritten.
                     *
                     * The START moves back; the end stays at now. Pushing the
                     * end forward instead produced an entry finishing half an
                     * hour from now, which validation rightly refused -- time
                     * logged by hand is time already worked. It is also what
                     * typing a length into the clock does when idle: that much
                     * time, ending now.
                     *
                     * A running timer needs none of this: it is producing its
                     * own length as it goes.
                     */
                    if (inManual && next && !manualSec) {
                      const to = parseClock(manualEnd)
                      if (to !== null) {
                        setManualStart(
                          clock((to - FEE_DEFAULT_SEC + DAY_SEC) % DAY_SEC, S.timeFormat),
                        )
                      }
                    }

                    pushFee(next, manualFee)
                  }}
                >
                  Fixed fee
                </button>
                {manualFeeOn && billValue && (
                  <input
                    className="time-input"
                    style={{ width: 96 }}
                    inputMode="decimal"
                    value={manualFee}
                    placeholder={manualCurrency}
                    aria-label={'Fixed fee for this entry, in ' + manualCurrency}
                    onChange={(e) => setManualFee(e.target.value)}
                    // On blur rather than on every keystroke: a running timer
                    // would otherwise be written to once per character.
                    onBlur={() => pushFee(manualFeeOn, manualFee)}
                  />
                )}
              </div>
            )}

            <div style={{ width: 1, height: 26, background: 'var(--line2)' }} />

            {inManual ? (
              <ManualFields
                start={manualStart}
                end={manualEnd}
                day={manualDay}
                setStart={setManualStart}
                setEnd={setManualEnd}
                setDay={setManualDay}
                sec={manualSec}
                onDuration={setManualLength}
              />
            ) : (
              <DurationEdit
                sec={running ? elapsed : 0}
                label={running ? hmsExact(elapsed) : hms(0)}
                seed={running ? hmsExact(elapsed) : undefined}
                className="elapsed"
                inputClassName="elapsed-input"
                title={
                  running
                    ? 'Click to correct how long this has been running'
                    : 'Click to log a length ending now — type 90 for 90 minutes'
                }
                onCommit={setElapsedTo}
              />
            )}

            <div className="row" style={{ gap: 8, flexShrink: 0 }}>
              <button
                type="button"
                className={running ? 'btn btn--ink' : 'btn btn--primary'}
                style={{ padding: '10px 22px', minWidth: 86, fontSize: 14 }}
                onClick={() => void primary()}
              >
                {primaryLabel}
              </button>
              {manualAllowed && (
                <div className="seg">
                  <button
                    type="button"
                    title="Timer mode"
                    aria-label="Timer mode"
                    aria-pressed={!manualMode}
                    onClick={() => setManualMode(false)}
                  >
                    ▶
                  </button>
                  <button
                    type="button"
                    title="Manual mode"
                    aria-label="Manual mode"
                    aria-pressed={manualMode}
                    onClick={() => setManualMode(true)}
                    disabled={!!running}
                  >
                    ☰
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Said before the button is pressed, not only after.
            A toast on Start answers "why did that not work"; this answers
            "will that work", which is the question somebody has while they
            are still typing. Only while idle: a timer already running is
            past the point this could help. */}
        {!running && pricedAtNothing(activeProjectId, null, todayKey()) && (
          <div
            className="live-strip"
            style={{ background: 'var(--dg-bg)', color: 'var(--dg)' }}
            role="status"
          >
            <span aria-hidden>⚠</span>
            {noRateMessage}
          </div>
        )}

        {running && !elsewhere && !card && (
          <div className="live-strip">
            <span className="live-dot" aria-hidden />
            Running since{' '}
            {clock(new Date(running.at).getHours() * 3600 + new Date(running.at).getMinutes() * 60, S.timeFormat)} ·{' '}
            {ws.projects.find((p) => p.id === running.projectId)?.name ?? 'No project'}
            {/* An unlit $ was the only sign that the clock running in front of
                you is earning nothing, which is no sign at all once you have
                stopped looking at the button. Said here in the same words the
                finished rows use, so it reads the same before and after. */}
            {!chargesForTime(
              running.billable,
              me.billable,
              ws.projects.find((p) => p.id === running.projectId)?.billable,
            ) && (
              <span className="entry-amt entry-amt--off" style={{ marginLeft: 2 }}>
                not charged
              </span>
            )}
          </div>
        )}
      </div>

      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {TIMER_RANGES.map((r) => (
          <button
            key={r}
            type="button"
            className="chip"
            aria-pressed={range === r}
            onClick={() => {
              setRange(r)
              // A list expanded to show more days should not stay expanded
              // into a range that has fewer.
              setVisibleDays(7)
            }}
          >
            {r}
          </button>
        ))}

        {range === 'Custom' && (
          <>
            <input
              type="date"
              className="input"
              style={{ width: 'auto', padding: '8px 10px' }}
              value={rangeFrom}
              max={rangeTo}
              onChange={(e) => e.target.value && setRangeFrom(e.target.value)}
              aria-label="From date"
            />
            <span style={{ color: 'var(--faint)' }}>–</span>
            <input
              type="date"
              className="input"
              style={{ width: 'auto', padding: '8px 10px' }}
              value={rangeTo}
              min={rangeFrom}
              onChange={(e) => e.target.value && setRangeTo(e.target.value)}
              aria-label="To date"
            />
          </>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
        <input
          className="input"
          style={{ flex: '1 1 200px', maxWidth: 320, padding: '9px 12px' }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Filter by text, project or ${noun}…`}
          aria-label={`Filter entries by text, project or ${noun}`}
        />
        {(
          [
            ['all', 'All'],
            ['billable', 'Billable'],
            ['nonbillable', 'Non-billable'],
          ] as const
        ).map(([id, label]) => (
          <button key={id} type="button" className="chip" aria-pressed={filter === id} onClick={() => setFilter(id)}>
            {label}
          </button>
        ))}
        <button type="button" className="btn" onClick={() => setModal({ kind: 'entry', scope: kind })}>
          + Add entry
        </button>
        {/* Shown only to the people the server will serve. export-csv runs
            requireAdmin, so a member pressing this would get a 403 dressed
            up as "Export failed" -- a button that is there and does not work
            is worse than one that is not there. */}
        {canSeeAll && (
          <button
            type="button"
            className="btn"
            onClick={exportLog}
            title={`Download these ${groups.count === 1 ? 'entry' : 'entries'} as a CSV`}
          >
            ↓ Export CSV
          </button>
        )}
        <div className="push" style={{ fontSize: 12.5, color: 'var(--mut)' }}>
          {groups.count} {groups.count === 1 ? 'entry' : 'entries'}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {shown.map((g) => (
          <div key={g.key} className="card card--clip">
            <div className="panel-head">
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 9 }}>
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>{dayLabel(g.key)}</span>
                <span style={{ fontSize: 12, color: 'var(--faint)' }}>{shortDate(g.key)}</span>
              </div>
              <span className="mono" style={{ fontSize: 13.5, fontWeight: 500 }}>
                {formatDur(g.sec, S.durFormat)}
              </span>
            </div>
            {g.clients.map((group) => {
              const groupKey = g.key + ':' + group.id
              const isCollapsed = collapsed.has(groupKey)
              return (
              <div key={group.id}>
                {/* Always shown, even when the day has only one client.
                    Hiding it then made the grouping invisible on exactly the
                    days most people have.

                    The subtotal stays visible when folded, so putting a
                    client away hides the detail without hiding the hours. */}
                <button
                  type="button"
                  className="spread"
                  aria-expanded={!isCollapsed}
                  onClick={() => toggleGroup(groupKey)}
                  style={{
                    width: '100%',
                    padding: '7px 16px',
                    background: 'var(--canvas)',
                    borderTop: '1px solid var(--line)',
                    borderLeft: 0,
                    borderRight: 0,
                    borderBottom: 0,
                    cursor: 'pointer',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                    color: 'var(--mut)',
                    gap: 8,
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      fontSize: 10,
                      transform: isCollapsed ? 'rotate(-90deg)' : 'none',
                      transition: 'transform .12s',
                    }}
                  >
                    ▾
                  </span>
                  <span className="trunc" style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    {group.name}
                  </span>
                  {isCollapsed && (
                    <span
                      style={{
                        fontWeight: 500,
                        letterSpacing: 0,
                        textTransform: 'none',
                        color: 'var(--faint)',
                      }}
                    >
                      {group.entries.length} {group.entries.length === 1 ? 'entry' : 'entries'}
                    </span>
                  )}
                  <span
                    className="mono"
                    style={{ fontWeight: 500, letterSpacing: 0, textTransform: 'none' }}
                  >
                    {formatDur(group.sec, S.durFormat)}
                  </span>
                </button>
                {!isCollapsed &&
                  group.entries.map((e) => (
                    <EntryRow
                      key={e.id}
                      entry={e}
                      scope={kind}
                      overlapping={overlaps.has(e.id)}
                      bill={billing.get(e.id)}
                      currency={currency}
                    />
                  ))}
              </div>
              )
            })}
          </div>
        ))}

        {groups.byDay.length > visibleDays && (
          <button
            type="button"
            className="btn"
            style={{ alignSelf: 'center' }}
            onClick={() => setVisibleDays((v) => v + 7)}
          >
            Show earlier days
          </button>
        )}

        {!groups.byDay.length &&
          (groups.count === 0 && !search && filter === 'all' && range === 'All' ? (
            <EmptyState
              title={prospecting ? 'No prospect time yet' : 'Nothing logged yet'}
              body={
                prospecting
                  ? 'Start the timer above against a prospect, or switch to manual and enter a range. Prospect time is kept out of the client reports and the client export, and is only ever reported here.'
                  : 'Start the timer above, or switch to manual and enter a range. Everything you track shows up here, grouped by day.'
              }
            />
          ) : (
            <EmptyState
              title={prospecting ? 'No prospect entries match' : 'No entries match'}
              body="Try a different search, or clear the billable filter."
              action={{
                label: 'Clear filters',
                onClick: () => {
                  setSearch('')
                  setFilter('all')
                },
              }}
            />
          ))}
      </div>

      {/* Only this side has one. Clients are managed on their own screen,
          and putting prospects there would show the two together. */}
      {prospecting && <ProspectAdmin />}

      {groups.count > 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--mut)' }}>
          Press <code className="kbd">Enter</code> in the description box to {running ? 'stop' : 'start'} the timer.
        </div>
      )}
    </section>
  )
}

/* ------------------------------------------------------------------ */

function BillButton({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className="bill-btn"
      aria-pressed={on}
      aria-label={on ? 'Billable — click to make non-billable' : 'Non-billable — click to make billable'}
      /* It said "Billable" whichever way it was set, so hovering the one state
         worth noticing described the other one. */
      title={on ? 'Billable — this time is charged' : 'Not charged — click to charge for this time'}
      onClick={onClick}
    >
      $
    </button>
  )
}

function ManualFields({
  start,
  end,
  day,
  setStart,
  setEnd,
  setDay,
  sec,
  large,
  onDuration,
}: {
  start: string
  end: string
  day: string
  setStart: (v: string) => void
  setEnd: (v: string) => void
  setDay: (v: string) => void
  sec: number | null
  large?: boolean
  /** Given a length, set the END so the range matches it. */
  onDuration?: (seconds: number) => void
}) {
  const cls = large ? 'time-input time-input--lg' : 'time-input'
  return (
    <div className="row" style={{ gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
      <input className={cls} value={start} onChange={(e) => setStart(e.target.value)} aria-label="Start time" />
      <span style={{ color: 'var(--faint)' }}>–</span>
      <input className={cls} value={end} onChange={(e) => setEnd(e.target.value)} aria-label="End time" />
      <input
        className="time-input date-input"
        type="date"
        value={day}
        onChange={(e) => e.target.value && setDay(e.target.value)}
        aria-label="Date"
      />
      {onDuration ? (
        <DurationEdit
          sec={sec ?? 0}
          label={sec === null ? '—' : hms(sec)}
          className={large ? 'manual-dur manual-dur--lg' : 'manual-dur'}
          inputClassName={large ? 'manual-dur-input manual-dur-input--lg' : 'manual-dur-input'}
          title="Click to set the length — type 90 for 90 minutes. The start moves back from the end time."
          onCommit={onDuration}
        />
      ) : (
        <span className={large ? 'manual-dur manual-dur--lg' : 'manual-dur'}>
          {sec === null ? '—' : hms(sec)}
        </span>
      )}
    </div>
  )
}

/**
 * An h:mm:ss that can be typed over.
 *
 * One component for both the running clock and each entry's length, so the
 * two cannot drift on what they accept or what they refuse. The caller says
 * what to DO with the number; everything about reading it -- a bare 90 being
 * ninety minutes, zero being read but rejected, the ten-hour limit -- is
 * decided here once.
 *
 * The committed value is the parsed seconds, never a re-read of the formatted
 * label, so a length is exactly what was typed rather than a round trip
 * through a rounded display.
 */
function DurationEdit({
  sec,
  label,
  seed,
  className,
  inputClassName,
  title,
  onCommit,
}: {
  sec: number
  label: string
  /**
   * What the box starts with, when that differs from the label -- the running
   * clock shows seconds and the default h:mm seed would drop them the moment
   * the box opened.
   */
  seed?: string
  className?: string
  /** Matched to `className` so the box does not resize the moment it is clicked. */
  inputClassName?: string
  title?: string
  onCommit: (seconds: number) => void
}) {
  const { flash } = useWorkspace()
  /** null means "not editing" -- distinct from an empty box being typed in. */
  const [text, setText] = useState<string | null>(null)
  /**
   * What the box was seeded with.
   *
   * The label is h:mm and the stored value has seconds, so seeding a 90-second
   * entry shows "0:01". Committing that would quietly round it to a minute --
   * a view-only change rewriting data. An untouched box therefore writes
   * nothing at all, rather than writing back what it happens to display.
   */
  const [seeded, setSeeded] = useState('')

  const commit = () => {
    if (text === null) return
    if (text === seeded) {
      // Opened and dismissed without an edit. Nothing to do.
      setText(null)
      return
    }
    const parsed = parseDurationInput(text)
    setText(null)

    if (parsed === null) {
      flash('Could not read that length. Try 90 for 90 minutes, or 1:30, or 1h30m.')
      return
    }
    if (parsed <= 0) {
      flash('A length has to be more than zero.')
      return
    }
    if (parsed > MAX_SESSION_SEC) {
      flash('An entry cannot be longer than 10 hours.')
      return
    }
    if (parsed === sec) return

    onCommit(parsed)
  }

  if (text === null) {
    return (
      <button
        type="button"
        className={className ? `dur-edit ${className}` : 'dur-edit'}
        title={title ?? 'Click to set the length — type 90 for 90 minutes'}
        onClick={() => {
          const initial = seed ?? hms(sec)
          setSeeded(initial)
          setText(initial)
        }}
      >
        {label}
      </button>
    )
  }

  /*
   * What the box has understood, as it is typed.
   *
   * A bare number is MINUTES here, which is the single thing people get
   * wrong: 3.3 is read as three and a bit minutes, and somebody who meant
   * three and a half hours gets 3m 18s with nothing to tell them. The parse
   * was never ambiguous -- it was invisible. Showing the answer before the
   * box is committed is what makes it arguable.
   */
  const reading = text.trim() ? parseDurationInput(text) : null

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <input
        autoFocus
        className={inputClassName ?? 'time-input'}
        style={inputClassName ? undefined : { width: 96 }}
        value={text}
        aria-label="Length"
        placeholder="90 or 1:30"
        onChange={(ev) => setText(ev.target.value)}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter') commit()
          if (ev.key === 'Escape') setText(null)
        }}
        onBlur={commit}
      />

      <div
        className="dur-hint"
        role="status"
        // Pointer events off: a mousedown in here would blur the input and
        // commit the entry before the click landed anywhere useful.
        style={{ pointerEvents: 'none' }}
      >
        <div className="dur-hint-read">
          {text.trim() === ''
            ? 'Type a length'
            : reading === null
              ? 'Not a length yet'
              : `= ${spellDuration(reading)}`}
        </div>
        <div className="dur-hint-help">
          <b>90</b> is 90 minutes · <b>1:30</b> or <b>1h30m</b> is an hour and a half
          <br />
          A plain number means minutes, so <b>3</b> is three minutes, not three hours
        </div>
      </div>
    </span>
  )
}

function EntryRow({
  entry: e,
  scope,
  overlapping,
  bill,
  currency,
}: {
  entry: Entry
  /** Which screen the row is on, so Edit opens the matching pickers. */
  scope: ClientKind
  overlapping: boolean
  /** How the database priced this entry. Absent until the prices arrive. */
  bill?: BillingRow
  currency: string
}) {
  const { ws, me, setModal, act, proj, flash, confirm } = useWorkspace()
  const S = ws.settings
  const p = proj(e.projectId)
  // The list only ever shows your own entries, so your own flag is the one
  // that applies to every row in it.
  const charges = chargesForTime(e.billable, me.billable, p?.billable)

  /**
   * The money chip, or nothing.
   *
   * Priced by the database, never worked out here. entry_billing resolves a
   * fixed amount, then a rate override, then the task and project prices, then
   * the hourly rates -- and a screen that reimplemented any part of that chain
   * is exactly how the figure on screen and the figure in the CSV came to
   * disagree about the same hours.
   *
   * Nothing at all when the row does not charge: the $ toggle at the other end
   * of the row already says so, and a second badge repeating it would crowd
   * the left of every non-billable row for no new information. Nothing while
   * the prices are still in flight either -- a blank for a moment beats a
   * confident 0.00.
   */
  const chip = !charges
    ? {
        text: 'not charged',
        cls: 'entry-amt entry-amt--off',
        why: 'Not billable. The hours still count, but they add nothing to the invoice.',
      }
    : !bill
      ? null
      : bill.amount !== null
        ? { text: formatMoney(bill.amount, currency), cls: 'entry-amt', why: '' }
        : bill.pricingModel === 'project_fixed'
          ? {
              text: 'flat fee',
              cls: 'entry-amt entry-amt--none',
              why: 'This project is billed at one flat price, counted once rather than spread across its entries.',
            }
          : {
              text: 'no rate',
              cls: 'entry-amt entry-amt--none',
              why: 'No charge-out rate applies to this entry, so it is currently billing nothing.',
            }

  /*
   * Whether that figure is a flat fee rather than hours times a rate.
   *
   * The amount alone cannot say so, and without it a fee set on the way in
   * looks exactly like an hour priced at the usual rate -- which is the one
   * thing somebody setting a fee wants to see afterwards. The Reports
   * breakdown has marked these since it was built; this row did not, so the
   * same entry said two different things in two places.
   *
   * Read from the view, like the amount beside it. A PROJECT priced flat is
   * not marked here: that amount is counted once at the project and never
   * reaches a row, which is why it reads "flat fee" in the chip instead.
   */
  const fixedFee =
    charges && (bill?.pricingModel === 'entry_fixed' || bill?.pricingModel === 'task_fixed')

  // Inline time editing. Correcting a start or end is the commonest edit
  // there is, and routing it through the modal made it a five-click job.
  const [editingTime, setEditingTime] = useState(false)
  const [startText, setStartText] = useState('')
  const [endText, setEndText] = useState('')

  // Same reasoning as the time above: fixing a typo in a description was a
  // trip through the modal for a single field.
  const [editingDesc, setEditingDesc] = useState(false)
  const [descText, setDescText] = useState('')
  // Escape has to beat the blur that follows it, or cancelling would save.
  const abandonDesc = useRef(false)

  const beginDescEdit = () => {
    setDescText(e.desc)
    abandonDesc.current = false
    setEditingDesc(true)
  }

  const saveDescEdit = async () => {
    setEditingDesc(false)
    if (abandonDesc.current) {
      abandonDesc.current = false
      return
    }
    const next = descText.trim()
    if (next === e.desc) return
    await act.updateEntry(e.id, { desc: next })
  }

  const beginTimeEdit = () => {
    setStartText(clock(e.start, S.timeFormat))
    setEndText(clock(e.start + e.dur, S.timeFormat))
    setEditingTime(true)
  }

  const saveTimeEdit = async () => {
    const from = parseClock(startText)
    const to = parseClock(endText)

    if (from === null || to === null) {
      flash('Use a time like 9:30, 09:30:15 or 2:05 pm.')
      return
    }
    // An end before the start reads as crossing midnight, which is what
    // someone means by 23:40 - 00:20 rather than a typo.
    const dur = to > from ? to - from : to + DAY_SEC - from
    if (dur <= 0 || dur > DAY_SEC) {
      flash('That range is not a valid length.')
      return
    }

    setEditingTime(false)
    if (from === e.start && dur === e.dur) return
    await act.updateEntry(e.id, { start: from, dur })
  }

  /*
   * Somebody else ended or amended this entry.
   *
   * guard_tracked_times stamps edited_by on every stop, your own included,
   * so the stamp alone says nothing -- it is the comparison with whose entry
   * this is that carries the meaning.
   */
  const stoppedByOther = Boolean(e.editedBy && e.editedBy !== e.userId)

  /*
   * Ended by the ten-hour limit rather than by a person.
   *
   * Detected rather than recorded: nothing marks a capped entry, so this
   * reads the shape the cap always leaves. stop_timer clamps ended_at to
   * started_at + 10 hours exactly, so the duration lands on the limit to the
   * second -- and trackedStart rules out a hand-written entry that happens
   * to be ten hours long, since only start_timer ever sets it.
   *
   * The cost of being wrong either way is small and the same: a prompt to
   * check an entry that is already sitting at the maximum a single entry can
   * hold, which is worth a look regardless.
   *
   * It belongs with the other case because the consequence is identical. The
   * hours are what the limit allowed, not what was worked -- probably fewer,
   * since the timer was running when it was cut off -- and the person who
   * did the work is the only one who can say what they should be.
   */
  const cappedAtLimit = e.trackedStart != null && e.dur === MAX_SESSION_SEC

  /*
   * Worth interrupting for, so it is said twice: a red surround that is
   * visible without reading, and a tag that says what to do about it.
   */
  const needsCheck = stoppedByOther || cappedAtLimit

  return (
    <div className={needsCheck ? 'entry-row entry-row--check' : 'entry-row'}>
      <span className="entry-bar" style={{ background: p?.color ?? 'var(--line)' }} aria-hidden />
      <div style={{ flex: '1 1 180px', minWidth: 0 }}>
        {editingDesc ? (
          <input
            autoFocus
            value={descText}
            aria-label="Description"
            onChange={(ev) => setDescText(ev.target.value)}
            onBlur={() => void saveDescEdit()}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter') ev.currentTarget.blur()
              else if (ev.key === 'Escape') {
                abandonDesc.current = true
                ev.currentTarget.blur()
              }
            }}
            style={{
              fontSize: 14,
              width: '100%',
              padding: '1px 6px',
              border: '1px solid var(--line)',
              borderRadius: 6,
              background: 'var(--surf)',
              color: 'inherit',
              font: 'inherit',
            }}
          />
        ) : (
          <button
            type="button"
            className="trunc"
            onClick={beginDescEdit}
            title="Click to edit the description"
            style={{
              fontSize: 14,
              width: '100%',
              textAlign: 'left',
              border: 0,
              background: 'transparent',
              padding: '1px 0',
              cursor: 'text',
              color: e.desc ? 'inherit' : 'var(--faint)',
              font: 'inherit',
            }}
          >
            {e.desc || 'No description'}
          </button>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 3, flexWrap: 'wrap' }}>
          <span
            className="entry-proj"
            style={
              p
                ? { color: 'var(--ink)', background: p.color + '1F', boxShadow: `inset 0 0 0 1px ${p.color}3D` }
                : { color: 'var(--faint)', background: 'var(--canvas)' }
            }
          >
            {p ? (e.task ? `${p.name} · ${e.task}` : p.name) : 'No project'}
          </span>
          {chip && (
            <span className={chip.cls} title={chip.why || undefined}>
              {chip.text}
            </span>
          )}
          {needsCheck && (
            <span
              className="entry-tag entry-tag--check"
              title={
                cappedAtLimit
                  ? 'This timer ran past ten hours, which is the longest a single entry can be, so it was stopped there. The hours are the limit rather than the work -- correct them if they are wrong.'
                  : 'Somebody else ended or changed this entry -- usually an owner stopping a timer that was left running. Check the times are right and correct them if they are not.'
              }
            >
              Please check
            </span>
          )}
          {fixedFee && (
            <span
              className="entry-tag"
              style={{ fontSize: 10.5 }}
              title="A flat fee for this entry. The duration beside it did not decide the amount."
            >
              fixed fee
            </span>
          )}
          {e.tags.map((t) => (
            <span key={t} className="entry-tag">
              {t}
            </span>
          ))}
        </div>
      </div>

      <button
        type="button"
        className="bill-btn bill-btn--sm"
        aria-pressed={charges}
        aria-label={charges ? 'Billable' : 'Non-billable'}
        disabled={!me.billable}
        title={me.billable ? 'Billable' : 'You are set as not charging for time'}
        onClick={() => {
          // Its own flag, not `charges` -- a project or person marked as not
          // charging makes the row read non-billable while the entry's own
          // flag is still on, and this button only ever moves that flag.
          if (!e.billable) {
            void act.updateEntry(e.id, { billable: true })
            return
          }

          // Naming the figure is the point: "stop charging" is abstract,
          // "US$1,600.00 will no longer be charged" is not.
          const worth = chip && chip.cls === 'entry-amt' ? chip.text : null

          confirm({
            title: 'Stop charging for this entry?',
            body: worth
              ? `${worth} will no longer be charged. The ${formatDur(e.dur, S.durFormat)} stays on the ` +
                'record and still counts towards the day, but it adds nothing to the invoice. ' +
                'You can switch it back at any time.'
              : `The ${formatDur(e.dur, S.durFormat)} stays on the record and still counts towards the ` +
                'day, but it will add nothing to the invoice. You can switch it back at any time.',
            cta: 'Make it non-billable',
            onConfirm: () => void act.updateEntry(e.id, { billable: false }),
          })
        }}
      >
        $
      </button>

      {editingTime ? (
        <span className="entry-range" style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          <input
            autoFocus
            className="time-input"
            style={{ width: 86 }}
            value={startText}
            aria-label="Start time"
            onChange={(ev) => setStartText(ev.target.value)}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter') void saveTimeEdit()
              if (ev.key === 'Escape') setEditingTime(false)
            }}
          />
          <span aria-hidden>–</span>
          <input
            className="time-input"
            style={{ width: 86 }}
            value={endText}
            aria-label="End time"
            onChange={(ev) => setEndText(ev.target.value)}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter') void saveTimeEdit()
              if (ev.key === 'Escape') setEditingTime(false)
            }}
            onBlur={() => void saveTimeEdit()}
          />
        </span>
      ) : (
        <button
          type="button"
          className="entry-range"
          style={{ background: 'none', border: 0, cursor: 'text', font: 'inherit', color: 'inherit' }}
          onClick={beginTimeEdit}
          title={
            e.trackedStart != null && e.trackedDur != null
              ? `Click to edit. Recorded by the timer: ${clock(
                  e.trackedStart,
                  S.timeFormat,
                )} – ${clock(e.trackedStart + e.trackedDur, S.timeFormat)} (${formatDur(
                  e.trackedDur,
                  'h:mm',
                )})`
              : 'Click to edit. Added by hand — never timed'
          }
        >
          {clock(e.start, S.timeFormat)} – {clock(e.start + e.dur, S.timeFormat)}
          {e.editedAt ? (
            <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--mut)', fontStyle: 'italic' }}>
              edited
            </span>
          ) : null}
          {overlapping ? (
            <span
              style={{ marginLeft: 6, fontSize: 11, color: 'var(--ac)', fontWeight: 600 }}
              title="This shares clock time with another of your entries. Both are counted, so these hours are billed twice. That is correct if you worked on them at once -- otherwise one is a duplicate."
            >
              overlaps
            </span>
          ) : null}
        </button>
      )}
      {/* Normally the start is kept and the END moves. When that would put
          the end in the future -- which the database refuses outright -- the
          block is anchored on its end and grown backwards instead, so the
          length is still exactly what was typed. fitDuration decides; it is
          tested against the case that produced the error. */}
      <DurationEdit
        sec={e.dur}
        label={formatDur(e.dur, S.durFormat)}
        className="entry-dur"
        title={`Click to set the length. Starts at ${clock(e.start, S.timeFormat)}; the end moves to match, or the start moves back if that would be in the future.`}
        onCommit={(seconds) => {
          const fit = fitDuration(e.day, e.start, e.dur, seconds)
          void act
            .updateEntry(e.id, { day: fit.day, start: fit.start, dur: fit.dur })
            .then((ok) => {
              if (ok && fit.movedBack) {
                flash('Start moved back, so the entry does not end in the future.')
              }
            })
        }}
      />

      <div className="entry-actions">
        <button
          type="button"
          className="ibtn ibtn--play"
          title="Continue"
          aria-label={`Continue ${e.desc}`}
          onClick={() => void act.continueEntry(e)}
        >
          ▶
        </button>
        <button
          type="button"
          className="ibtn"
          title="Edit"
          aria-label={`Edit ${e.desc}`}
          onClick={() => setModal({ kind: 'entry', scope, entry: e })}
        >
          ✎
        </button>
        <button
          type="button"
          className="ibtn"
          title="Duplicate"
          aria-label={`Duplicate ${e.desc}`}
          onClick={() => void act.duplicateEntry(e)}
        >
          ⧉
        </button>
        <button
          type="button"
          className="ibtn ibtn--del"
          title="Delete"
          aria-label={`Delete ${e.desc}`}
          onClick={() => void act.deleteEntry(e.id)}
        >
          ✕
        </button>
      </div>
    </div>
  )
}
